// Shared AdSense eligibility helper.
// Determines whether the current visitor should see ads based on:
//   - server-side settings (ads_enabled, publisher ID, africa exclusion flag)
//   - resolved cookie consent
//   - visitor country (Cloudflare CF-IPCountry surfaced via /api/geo/me)
import { useEffect, useState } from 'react';
import axios from 'axios';

const CONSENT_KEY = 'christovibes:cookie_consent:v1';
const CONSENT_EVENT = 'christovibes:consent-changed';
const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw)?.choice || null;
  } catch { return null; }
}

export function validPublisher(id) { return /^ca-pub-\d{16}$/.test(id || ''); }
export function validSlot(id) { return /^\d{10}$/.test(id || ''); }

let _settingsPromise = null;
let _geoPromise = null;

export function fetchAdsSettings() {
  if (!_settingsPromise) {
    _settingsPromise = axios.get(`${API}/settings/ads`)
      .then(r => r.data)
      .catch(() => ({ ads_enabled: false }));
  }
  return _settingsPromise;
}

export function fetchGeo() {
  if (!_geoPromise) {
    _geoPromise = axios.get(`${API}/geo/me`)
      .then(r => r.data || { country: '', is_african: false })
      .catch(() => ({ country: '', is_african: false }));
  }
  return _geoPromise;
}

export function useAdsEligibility() {
  const [settings, setSettings] = useState(null);
  const [geo, setGeo] = useState(null);
  const [consent, setConsent] = useState(readConsent());

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchAdsSettings(), fetchGeo()]).then(([s, g]) => {
      if (!cancelled) { setSettings(s); setGeo(g); }
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onChange = (e) => setConsent(e?.detail?.choice ?? readConsent());
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  const publisherId = settings?.ads_publisher_id || process.env.REACT_APP_ADSENSE_CLIENT_ID || '';
  const regionBlocked = !!(settings?.ads_africa_disabled && geo?.is_african);
  const enabled = !!settings?.ads_enabled && !!consent && validPublisher(publisherId) && !regionBlocked;

  return { settings, geo, consent, publisherId, enabled, regionBlocked };
}
